import { DEFINITIONS } from '../utils/constants';
/**
 * @description Sets up event listeners on tag checkboxes to update active tags in state and re-apply filters.
 * @param {HTMLElement} context - The section container element.
 * @param {Object} options - Configuration options.
 * @param {Object} options.state - The application state object.
 * @param {Object} options.map - The Google Map instance.
 * @return {void}
 */
export const initTagsUI = (context, { state, map, apply }) => {
  const tagsContainer = context.querySelector(`.${DEFINITIONS.slug}__tags__list`);
  if (!tagsContainer) return;

  tagsContainer.querySelectorAll(`.${DEFINITIONS.slug}__tags__list-item`).forEach((tagItem) => {
    const checkbox = tagItem.querySelector('input[type="checkbox"]');
    if (!checkbox) return;

    const tag = tagItem.dataset.tag;

    checkbox.addEventListener('change', () => {
      if (checkbox.checked) state.activeTags.add(tag);
      else state.activeTags.delete(tag);

      // Re-apply with the new tag selection
      apply(context, { state, map });
    });
  });
};

/**
 * @description Synchronizes the tags UI based on the current viewport stores and active tags.
 * Updates tag visibility, checked state, and disabled state.
 * @param {HTMLElement} context - The section container element.
 * @param {Object} options - Configuration options.
 * @param {Array} options.allTags - All available tags.
 * @param {Set} options.activeTags - Currently active/selected tags.
 * @param {Array} options.viewportStores - Stores currently visible in the map viewport.
 * @returns {void}
 */
export const syncTagsUI = (context, { allTags, activeTags, viewportStores }) => {
  const tagsContainer = context.querySelector(`.${DEFINITIONS.slug}__tags__list`);
  if (!tagsContainer) return;

  // Count occurrences of tags within the current viewport (regardless of filters)
  const counts = new Map();
  viewportStores.forEach((store) => {
    if (Array.isArray(store.tags)) {
      store.tags.forEach(({ label }) => {
        if (!label) return;
        counts.set(label, (counts.get(label) || 0) + 1);
      });
    }
  });

  // Update each tag item
  allTags.forEach((tag) => {
    const tagItem = tagsContainer.querySelector(`.${DEFINITIONS.slug}__tags__list-item[data-tag="${tag}"]`);
    if (!tagItem) return;

    const checkbox = tagItem.querySelector('input[type="checkbox"]');
    if (!checkbox) return;

    const countInViewport = counts.get(tag) || 0;

    // Never hide a checked tag; hide only if count=0 AND not checked
    const shouldHide = countInViewport === 0 && !activeTags.has(tag);
    tagItem.style.display = shouldHide ? 'none' : '';

    // Don't force uncheck; preserve user's choice
    if (!shouldHide) {
      checkbox.checked = activeTags.has(tag);
    }

    // Disable tag if not present in viewport and not already checked
    checkbox.disabled = countInViewport === 0 && !activeTags.has(tag);

    // if every tag is hidden, hide the entire tags container
    const anyVisible = Array.from(tagsContainer.children).some((item) => item.style.display !== 'none');
    const tagsWrapper = context.querySelector(`.${DEFINITIONS.slug}__tags`);
    if (tagsWrapper) {
      tagsWrapper.classList.toggle(`${DEFINITIONS.slug}__tags--no-tags`, !anyVisible);
    }
  });
};
